import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Link } from '../components/Links';
import { Customer, CustomerInput, customers } from '../api';
import { DataGrid } from '../components/DataGrid';
import { dayOnly } from '../format';

const BLANK: CustomerInput = { name: '', contactName: '', phone: '', email: '', notes: '' };

export function CustomerFields({ f, setF }: { f: CustomerInput; setF: (f: CustomerInput) => void }) {
  const str = (k: keyof CustomerInput) => ({ value: f[k] ?? '', onChange: (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value }) });
  return (
    <>
      <div className="grid2">
        <label>Name<input id="c-name" type="text" required maxLength={100} {...str('name')} /></label>
        <label>Contact person<input id="c-contact" type="text" maxLength={100} {...str('contactName')} /></label>
        <label>Phone<input id="c-phone" type="tel" maxLength={40} {...str('phone')} /></label>
        <label>Email<input id="c-email" type="email" maxLength={200} {...str('email')} /></label>
      </div>
      <label>Notes<textarea id="c-notes" rows={3} maxLength={5000} {...str('notes')} /></label>
    </>
  );
}

export function Customers() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const [typed, setTyped] = useState(q);
  const [rows, setRows] = useState<Customer[] | null>(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      if (typed.trim() !== q) setParams((p) => { typed.trim() ? p.set('q', typed.trim()) : p.delete('q'); return p; }, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [typed, q, setParams]);

  useEffect(() => {
    customers.list(q).then((r) => setRows(r.customers)).catch((e) => setError(e.message));
  }, [q]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setFormError('');
    try {
      const res = await customers.add(f);
      navigate(`/customers/${res.id}`);
    } catch (err) {
      setFormError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="page wide">
      <div className="page-head">
        <h1>Customers</h1>
        {!adding && <button className="primary" onClick={() => setAdding(true)}>Add customer</button>}
      </div>
      {error && <p className="error" role="alert">{error}</p>}
      {adding && (
        <form className="card form" onSubmit={submit}>
          <h2>Add a customer</h2>
          <CustomerFields f={f} setF={setF} />
          {formError && <p className="error" role="alert">{formError}</p>}
          <div className="actions">
            <button className="primary" disabled={busy}>{busy ? 'Adding…' : 'Add customer'}</button>
            <button type="button" className="link" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </form>
      )}
      <div className="toolbar">
        <label className="sr-only" htmlFor="cust-search">Search</label>
        <input id="cust-search" type="search" placeholder="Search name, contact, email…" value={typed} onChange={(e) => setTyped(e.target.value)} />
      </div>
      {rows && (
        <DataGrid id="customers" rows={rows} rowKey={(c) => c.id} noun={['customer', 'customers']} href={(c) => `/customers/${c.id}`}
          defaultSort={{ key: 'name', dir: 1 }} empty={q ? 'No customers match.' : 'No customers yet.'} columns={[
            { key: 'name', label: 'Name', always: true, value: (c) => c.name, cell: (c) => <Link to={`/customers/${c.id}`}><strong>{c.name}</strong></Link> },
            { key: 'contact', label: 'Contact', value: (c) => c.contactName },
            { key: 'email', label: 'Email', value: (c) => c.email },
            { key: 'phone', label: 'Phone', value: (c) => c.phone, nowrap: true },
            { key: 'vehicles', label: 'Vehicles', align: 'right', value: (c) => c.vehicles ?? 0 },
            { key: 'added', label: 'Added', hidden: true, value: (c) => c.createdAt, sortBy: (c) => (c.createdAt ? Date.parse(c.createdAt) : null),
              csv: (c) => (c.createdAt ? dayOnly(c.createdAt) : null), cell: (c) => (c.createdAt ? dayOnly(c.createdAt) : <span className="muted">–</span>), nowrap: true },
            { key: 'notes', label: 'Notes', hidden: true, value: (c) => c.notes, nosort: true },
          ]} />
      )}
    </div>
  );
}
